import { Button, Modal, ModalContent, ModalHeader } from '@strivelabs/ui-core/v2';

/** Names shown before the dialog switches to "and N more". */
const NAME_PREVIEW_LIMIT = 8;

export interface DeleteDialogProps {
  open: boolean;
  /** Chats pending deletion. */
  count: number;
  /**
   * What is about to be destroyed, for a bulk delete. Chats are visible across
   * the whole workspace, so a count alone lets someone confirm away other
   * people's threads without ever seeing whose.
   */
  names?: string[];
  pending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

/** Confirms deleting one or more chats. Deletion is not recoverable. */
export function DeleteDialog({
  open,
  count,
  names,
  pending,
  onCancel,
  onConfirm,
}: DeleteDialogProps) {
  if (!open) return null;

  const isSingle = count === 1;
  const title = isSingle ? 'Delete chat?' : `Delete ${count} chats?`;
  const subject = isSingle ? 'This chat' : 'These chats';
  const possessive = isSingle ? 'its' : 'their';
  /** Enough to recognise the selection without turning the dialog into a list. */
  const listed = names?.slice(0, NAME_PREVIEW_LIMIT) ?? [];
  const remaining = (names?.length ?? 0) - listed.length;

  return (
    <Modal open onOpenChange={next => !next && onCancel()}>
      <ModalContent className="w-[420px] p-0" ariaLabel={title}>
        <ModalHeader
          title={title}
          description={`${subject} and ${possessive} history will be removed. This cannot be undone.`}
        />
        {listed.length > 0 ? (
          <ul className="max-h-40 overflow-y-auto px-6 pb-4 text-v2-sm text-v2-foreground">
            {listed.map((name) => (
              <li key={name} className="truncate py-0.5">
                {name}
              </li>
            ))}
            {remaining > 0 ? (
              <li className="py-0.5 text-v2-muted-foreground">and {remaining} more</li>
            ) : null}
          </ul>
        ) : null}
        <div className="flex justify-end gap-2 px-6 pb-6">
          <Button variant="borderless" size="sm" type="button" onClick={onCancel} disabled={pending}>
            Cancel
          </Button>
          <Button tone="danger" size="sm"
            type="button"
            onClick={onConfirm}
            state={pending ? 'loading' : undefined}
          >
            Delete
          </Button>
        </div>
      </ModalContent>
    </Modal>
  );
}
